import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { allArticles } from '@/mocks/articles';

export default function SEOArticle() {
  const { t } = useTranslation('home');
  const article = allArticles.find((a) => a.slug === 'cssbuy-sheet-guia-completo')!;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        image: article.imageUrl,
        datePublished: article.date,
        dateModified: article.date,
        author: { '@type': 'Organization', name: article.author, url: 'https://cssbuy.watch' },
        publisher: { '@type': 'Organization', name: 'CSSBuy.watch', url: 'https://cssbuy.watch', logo: { '@type': 'ImageObject', url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png' } },
        mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://cssbuy.watch/cssbuy-sheet-guia-completo' },
        inLanguage: 'pt-BR',
      },
      ...(article.faq.length > 0 ? [{
        '@type': 'FAQPage',
        mainEntity: article.faq.map((f: { question: string; answer: string }) => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: f.answer,
          },
        })),
      }] : []),
    ],
  };

  return (
    <main className="min-h-screen bg-background-50">
      <article>
        <header className="relative w-full pt-24 pb-12 md:pt-32 md:pb-16 bg-foreground-900">
          <div className="absolute inset-0 opacity-[0.04]">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20minimalist%20texture%20with%20flowing%20organic%20shapes%20in%20warm%20terracotta%20and%20sand%20tones%20subtle%20gradient%20editorial%20design%20aesthetic%20clean%20and%20modern%20sophisticated%20background&width=1600&height=600&seq=art-header-main-guide-v3&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10">
            <div className="flex items-center gap-2 text-sm text-background-50/50 mb-4">
              <Link
                to="/"
                className="hover:text-background-50/80 transition-colors whitespace-nowrap"
              >
                {t('article_breadcrumb_home')}
              </Link>
              <span>/</span>
              <span>{t('article_breadcrumb_blog')}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight max-w-3xl">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-6 text-sm text-background-50/50">
              <span>{article.author}</span>
              <span className="w-1 h-1 rounded-full bg-background-50/20"></span>
              <span>{t('article_published')}</span>
              <span className="w-1 h-1 rounded-full bg-background-50/20"></span>
              <span>{t('article_reading_time')}</span>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16">
          <div
            className="prose prose-lg max-w-none
              prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-bold
              prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-5
              prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-10 prose-h3:mb-4
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:text-sm md:prose-p:text-base prose-p:mb-5
              prose-li:text-foreground-700 prose-li:text-sm md:prose-li:text-base prose-li:mb-1.5
              prose-ul:my-5 prose-ul:pl-5
              prose-strong:text-foreground-900
              [&_a]:text-primary-600 [&_a]:underline [&_a]:underline-offset-2"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {article.faq.length > 0 && (
            <div className="mt-16 p-6 md:p-8 bg-background-100 rounded-2xl border border-secondary-100">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-question-answer-line text-primary-500"></i>
                </div>
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
                  Perguntas Frequentes
                </h2>
              </div>
              <div className="space-y-4">
                {article.faq.map((item, idx) => (
                  <details key={idx} className="group">
                    <summary className="flex items-center justify-between cursor-pointer py-3 text-sm md:text-base font-medium text-foreground-800 hover:text-foreground-950 transition-colors">
                      {item.question}
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 ml-3">
                        <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-foreground-400"></i>
                      </div>
                    </summary>
                    <p className="pb-3 text-sm text-foreground-600 leading-relaxed">
                      {item.answer}
                    </p>
                  </details>
                ))}
              </div>
            </div>
          )}

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 p-6 bg-primary-50 rounded-2xl border border-primary-100">
            <div>
              <p className="text-sm text-foreground-700">
                Gostou deste artigo? Volte para explorar mais categorias.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/"
                className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer text-center"
              >
                {t('article_back_home')}
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-2.5 border-2 border-primary-300 text-primary-700 rounded-full text-sm font-semibold hover:bg-primary-100 transition-all duration-200 whitespace-nowrap cursor-pointer text-center"
              >
                Acessar CSSBuy Sheet
              </a>
            </div>
          </div>
        </div>
      </article>

      <footer className="w-full bg-foreground-900 py-8">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Link
            to="/"
            className="text-sm text-background-50/50 hover:text-background-50/80 transition-colors whitespace-nowrap"
          >
            &larr; Voltar ao Inicio — CSSBuy.watch
          </Link>
          <p className="mt-4 text-xs text-background-50/30">
            &copy; 2026 CSSBuy.watch. Todos os direitos reservados.
          </p>
        </div>
      </footer>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </main>
  );
}